import { startOfDay, endOfDay } from 'date-fns';
import type { Booking } from '@/components/booking-grid/types';

/**
 * Результат проверки конфликта бронирований
 */
export interface BookingConflictResult {
  hasConflict: boolean;
  conflictingBookings: Booking[];
  reason?: string;
}

/**
 * Результат валидации формы бронирования
 */
export interface BookingFormValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

/**
 * Проверяет пересечение двух временных интервалов
 */
function doDateRangesOverlap(
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean {
  // Нормализуем даты к началу дня для корректного сравнения
  const s1 = startOfDay(start1).getTime();
  const e1 = startOfDay(end1).getTime();
  const s2 = startOfDay(start2).getTime();
  const e2 = startOfDay(end2).getTime();
  
  // Интервалы пересекаются если:
  // start1 < end2 AND end1 > start2
  return s1 < e2 && e1 > s2;
}

/**
 * Проверяет конфликт бронирования с существующими бронированиями
 */
export function checkBookingConflict(
  roomId: string,
  checkIn: Date,
  checkOut: Date,
  existingBookings: Booking[],
  excludeBookingId?: string
): BookingConflictResult {
  // Фильтруем бронирования для этого номера
  const roomBookings = existingBookings.filter(
    booking => 
      booking.roomId === roomId && 
      booking.id !== excludeBookingId // Исключаем текущее бронирование при перемещении
  );

  // Проверяем пересечения с каждым бронированием
  const conflictingBookings = roomBookings.filter(booking => 
    doDateRangesOverlap(checkIn, checkOut, booking.checkIn, booking.checkOut)
  );

  if (conflictingBookings.length > 0) {
    const conflictDetails = conflictingBookings
      .map(b => `${b.guest?.name || 'Гость'} (${startOfDay(b.checkIn).toLocaleDateString()} - ${startOfDay(b.checkOut).toLocaleDateString()})`)
      .join(', ');
      
    return {
      hasConflict: true,
      conflictingBookings,
      reason: `Конфликт с бронированиями: ${conflictDetails}`
    };
  }

  return {
    hasConflict: false,
    conflictingBookings: []
  };
}

/**
 * Проверяет возможность переселения бронирования
 */
export function canRelocateBooking(
  booking: Booking,
  targetRoomId: string,
  targetCheckIn: Date,
  targetCheckOut: Date,
  allBookings: Booking[]
): BookingConflictResult {
  return checkBookingConflict(
    targetRoomId,
    targetCheckIn,
    targetCheckOut,
    allBookings,
    booking.id // Исключаем само бронирование из проверки
  );
}

/**
 * Находит первый свободный номер в категории для заданных дат
 */
export function findAvailableRoom(
  categoryId: string,
  checkIn: Date,
  checkOut: Date,
  rooms: Array<{ id: string; categoryId: string }>,
  existingBookings: Booking[]
): string | null {
  // Получаем все номера в этой категории
  const categoryRooms = rooms.filter(room => room.categoryId === categoryId);
  
  // Проверяем каждый номер на доступность
  for (const room of categoryRooms) {
    const conflict = checkBookingConflict(
      room.id,
      checkIn,
      checkOut,
      existingBookings
    );
    
    if (!conflict.hasConflict) {
      return room.id;
    }
  }
  
  return null;
}

/**
 * Получает список всех свободных номеров для заданных дат
 */
export function getAvailableRooms(
  checkIn: Date,
  checkOut: Date,
  rooms: Array<{ id: string; categoryId: string; number: string }>,
  existingBookings: Booking[]
): Array<{ id: string; number: string; categoryId: string }> {
  return rooms.filter(room => {
    const conflict = checkBookingConflict(
      room.id,
      checkIn,
      checkOut,
      existingBookings
    );
    return !conflict.hasConflict;
  });
}

/**
 * Валидирует форму бронирования
 */
export async function validateBookingForm(
  formData: any,
  bookingId?: string
): Promise<BookingFormValidationResult> {
  const errors: Record<string, string> = {};

  // Валидация имени гостя
  if (!formData.guest_name && !formData.guest_first_name) {
    errors.guest_name = 'Укажите имя гостя';
  }

  // Валидация дат
  if (!formData.check_in_date) {
    errors.check_in_date = 'Укажите дату заезда';
  }

  if (!formData.check_out_date) {
    errors.check_out_date = 'Укажите дату выезда';
  }

  // Проверка что дата выезда позже даты заезда
  if (formData.check_in_date && formData.check_out_date) {
    const checkIn = new Date(formData.check_in_date);
    const checkOut = new Date(formData.check_out_date);
    
    if (checkOut <= checkIn) {
      errors.check_out_date = 'Дата выезда должна быть позже даты заезда';
    }
  }

  // Валидация номера телефона (базовая)
  if (formData.guest_phone && formData.guest_phone.length < 10) {
    errors.guest_phone = 'Некорректный номер телефона';
  }

  // Валидация email (базовая)
  if (formData.guest_email && !formData.guest_email.includes('@')) {
    errors.guest_email = 'Некорректный email';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}

